// Undo on the line Board activity keeps for an agent's change to a sheet that cleared or wrote over filled cells
// (Fixes 9): the change taken back from the trash row that keeps it, over the sheet as it is now, so a cell written
// since stays. Once it is back, the line says so in place of the button. The owner's alone: a shared view never gets it.

import { useState } from "react";
import type { ID } from "../../shared/types";
import { api } from "../lib/api";
import { toast } from "../state/store";
import { UndoButton } from "./UndoButton";

const done = new Set<ID>();

/** Takes an agent's change to a sheet back from the trash; says what stayed. */
export async function undoSheetChange(trash: ID): Promise<boolean> {
  const { flush, resync } = await import("../state/sync");
  await flush().catch(() => null);
  let kept = 0;
  try {
    const r = await api<{ kept?: number }>(`/api/trash/${encodeURIComponent(trash)}/restore`, { body: {} });
    kept = r.kept ?? 0;
  } catch (e) {
    const msg = (e as Error).message;
    const gone = /not in the trash/i.test(msg);
    // its cells were changed again since (a later change of the agent's): nothing went back, and it waits in the trash
    const later = /^Nothing was put back: /.test(msg);
    toast({ level: gone || later ? "info" : "error", title: gone ? "Already put back" : later ? "Nothing put back yet" : "Could not put it back", body: gone ? "That change was taken back already, or its 30 days in the trash are over." : later ? msg.replace(/^Nothing was put back: /, "").replace(/^./, (c) => c.toUpperCase()) : msg });
    if (gone) done.add(trash);
    return gone;
  }
  // cells that stayed wait in the trash, so the line keeps its Undo for them
  if (!kept) done.add(trash);
  await resync();
  toast({ level: "success", title: "Put back", body: kept ? `${kept === 1 ? "One cell was" : `${kept} cells were`} changed since, so ${kept === 1 ? "it stays" : "they stay"} as ${kept === 1 ? "it is" : "they are"}, and ${kept === 1 ? "waits" : "wait"} in the trash: undo the later change, then this one again.` : "The cells are as they were before the change.", key: `sheet-undo:${trash}` });
  return !kept;
}

export function SheetUndo({ trash }: { trash: ID }) {
  const [back, setBack] = useState(done.has(trash));
  if (back) return <span className="fd-undone">Put back</span>;
  return <UndoButton tip="Puts back what the agent cleared or wrote over" run={() => undoSheetChange(trash).then((ok) => ok && setBack(true))} />;
}
